import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { AppButton, AppIconButton } from '../design-system';
import TempFileBrowser from './TempFileBrowser';
import type { TempFileMetadata } from '@cnapse/shared/temp-file-schemas.js';
import { tempFileMatchesMediaKind, tempFilePreviewUrl } from './tempFileBrowserUtils';

export interface TempFileImportItem {
  fileId: number;
  previewUrl: string;
  file: TempFileMetadata;
}

export interface TempFilePickerDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (files: TempFileImportItem[]) => void;
  mediaKind?: 'image' | 'document' | 'any';
  title?: string;
  maxSelection?: number;
}

export const TempFilePickerDialog: React.FC<TempFilePickerDialogProps> = ({
  open,
  onClose,
  onImport,
  mediaKind = 'any',
  title,
  maxSelection,
}) => {
  const dialogTitle =
    title ??
    (mediaKind === 'image'
      ? 'Browse temp images'
      : mediaKind === 'document'
        ? 'Browse temp documents'
        : 'Browse temp files');
  const selectionNoun = mediaKind === 'image' ? 'images' : mediaKind === 'document' ? 'documents' : 'files';

  const [stagedFiles, setStagedFiles] = useState<TempFileMetadata[]>([]);

  useEffect(() => {
    if (!open) {
      setStagedFiles([]);
    }
  }, [open]);

  const toggleStage = useCallback(
    (file: TempFileMetadata) => {
      if (!tempFileMatchesMediaKind(file, mediaKind)) return;
      setStagedFiles((prev) => {
        const exists = prev.some((f) => f.id === file.id);
        if (exists) return prev.filter((f) => f.id !== file.id);
        if (maxSelection != null && prev.length >= maxSelection) return prev;
        return [...prev, file];
      });
    },
    [maxSelection, mediaKind],
  );

  const handleImport = () => {
    if (stagedFiles.length === 0) return;
    onImport(
      stagedFiles.map((file) => ({
        fileId: file.id,
        previewUrl: tempFilePreviewUrl(file) ?? '',
        file,
      })),
    );
    onClose();
  };

  const stagedIds = stagedFiles.map((f) => f.id);
  const atCapacity = maxSelection != null && stagedFiles.length >= maxSelection;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      scroll="paper"
      slotProps={{ paper: { sx: { minHeight: 'min(80vh, 900px)' } } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6">{dialogTitle}</Typography>
            <Typography variant="caption" color="text.secondary">
              Select {selectionNoun}, then click Import
              {maxSelection != null ? ` (up to ${maxSelection})` : ''}
            </Typography>
          </Box>
          <AppIconButton onClick={onClose} aria-label="Close">
            <Close />
          </AppIconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, pt: 3, pb: 2, overflow: 'visible' }}>
        <TempFileBrowser
          mediaKind={mediaKind}
          selectionMode
          deferredSelection
          embedded
          stagedFileIds={stagedIds}
          onToggleStage={toggleStage}
          maxSelection={maxSelection}
          gridCols={4}
          gridTileHeight={280}
          listThumbnailSize={96}
          listMaxHeight="min(68vh, 720px)"
          pageSize={50}
        />
        {atCapacity ? (
          <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
            Selection limit reached.
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: 0 }}>
        <AppButton variant="outlined" onClick={onClose}>
          Cancel
        </AppButton>
        <AppButton variant="contained" disabled={stagedFiles.length === 0} onClick={handleImport}>
          Import{stagedFiles.length > 0 ? ` (${stagedFiles.length})` : ''}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default TempFilePickerDialog;
